import { FC, useEffect, useRef, useState } from "react"
import { Formik, FormikProps } from "formik"
import { object, string, number } from "yup"
import SidePanel from "../../../side-panel"
import Tabs from "../../../tabs"
import { ConnectorDetails, ConnectorFormValues } from "../../../../../types"
import ManageConnectorFormContent from "./form-content"
import TestConnector from "./test-connector"
import ConnectorLogs from "./logs"
import {
  useFetchAllBrandsQuery,
  useUpdateOrCreateConnectorMutation
} from "../../../../../redux/queries"
import { useAppDispatch } from "../../../../hooks"
import { pushToast } from "../../../../../redux/slices/notifications"

interface Props {
  open: boolean
  data?: ConnectorDetails
  brandId: string
  onClose: () => void
}

const ConnectorSchema = object({
  name: string().required("Name is required").min(1, "Name is required"),
  type: string().required("Type is required"),
  brand_id: string().required("Brand is required"),
  owner: string().required("Owner is required"),
  direction: string().required("Direction is required"),
  data_type: string().required("Data type is required"),
  webhook_url: string().when("direction", {
    is: (val: string) => val === "outgoing" || val === "bidirectional",
    then: schema =>
      schema.required("Webhook URL is required for outgoing connectors"),
    otherwise: schema => schema.notRequired()
  }),
  event: string().when("direction", {
    is: (val: string) => val === "outgoing" || val === "bidirectional",
    then: schema =>
      schema.required("Event is required for outgoing connectors"),
    otherwise: schema => schema.notRequired()
  }),
  template: string().when("direction", {
    is: (val: string) => val === "outgoing" || val === "bidirectional",
    then: schema =>
      schema.required("Template is required for outgoing connectors"),
    otherwise: schema => schema.notRequired()
  }),
  field_mapping: object().required("Field mapping is required"),
  config: object(),
  active: number()
})

const ManageConnector: FC<Props> = ({ open, data, brandId, onClose }) => {
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState<string>("details")

  const formRef = useRef<FormikProps<ConnectorFormValues>>(null)

  const { data: brands } = useFetchAllBrandsQuery({ limit: 999 })
  const [
    updateOrCreate,
    {
      data: updateOrCreateResponse,
      isSuccess,
      isError,
      reset: resetUpdateOrCreateResponse
    }
  ] = useUpdateOrCreateConnectorMutation()

  const isCreate = !data?.id

  // Reset to details tab when opening
  useEffect(() => {
    if (open) {
      setActiveTab("details")
    } else {
      // Reset form when closing to discard unsaved changes
      formRef.current?.resetForm()
    }
  }, [open])

  // Parse config and field_mapping if they're strings
  const parseJSONField = (field: any) => {
    if (typeof field === "string") {
      try {
        return JSON.parse(field)
      } catch {
        return {}
      }
    }
    // Return object if it's an object (not array), otherwise return empty object
    return field && typeof field === "object" && !Array.isArray(field)
      ? field
      : {}
  }

  // Create stable JSON string with sorted keys for deep comparison
  const stableStringify = (obj: any): string => {
    if (obj === null || obj === undefined) return JSON.stringify(obj)
    if (typeof obj !== "object") return JSON.stringify(obj)
    if (Array.isArray(obj)) return JSON.stringify(obj)

    // Recursively sort all nested objects
    const sortedObj: any = {}
    Object.keys(obj)
      .sort()
      .forEach(key => {
        const value = obj[key]
        if (value && typeof value === "object" && !Array.isArray(value)) {
          // Recursively sort nested objects
          sortedObj[key] = JSON.parse(stableStringify(value))
        } else {
          sortedObj[key] = value
        }
      })
    return JSON.stringify(sortedObj)
  }

  const initialValues: ConnectorFormValues = data
    ? {
        id: data.id,
        name: data.name,
        type: data.type,
        brand_id: data.brand_id,
        owner: data.owner,
        direction: data.direction,
        data_type: data.data_type,
        webhook_url: data.webhook_url || "",
        event: data.event || "",
        field_mapping: parseJSONField(data.field_mapping),
        template:
          typeof data.template === "string"
            ? data.template
            : data.template
            ? JSON.stringify(data.template, null, 2)
            : "",
        config: parseJSONField(data.config),
        active: data.active,
        incoming_webhook_url: data.incoming_webhook_url || ""
      }
    : {
        name: "",
        type: "webhook",
        brand_id: brandId,
        owner: "",
        direction: "outgoing",
        data_type: "lead",
        webhook_url: "",
        event: "lead.created",
        field_mapping: {},
        template: "",
        config: {},
        active: 1
      }

  const setFormField = (name: string, val: any) => {
    formRef.current?.setFieldValue(name, val)
  }

  const onSubmit = () => {
    if (formRef.current?.values) {
      const payload = { ...formRef.current.values }

      console.log("Submitting connector:", payload)

      // Ensure field_mapping and config are objects not strings
      if (typeof payload.field_mapping === "string") {
        try {
          payload.field_mapping = JSON.parse(payload.field_mapping)
        } catch {
          payload.field_mapping = {}
        }
      }

      if (typeof payload.config === "string") {
        try {
          payload.config = JSON.parse(payload.config)
        } catch {
          payload.config = {}
        }
      }

      // Ensure active is a number
      if (typeof payload.active === "string") {
        payload.active = parseInt(payload.active)
      }

      console.log("Processed payload:", payload)

      updateOrCreate(payload)
    }
  }

  useEffect(() => {
    if (isSuccess && updateOrCreateResponse?.status === "success") {
      console.log("Update/create success response:", updateOrCreateResponse)

      dispatch(
        pushToast({
          msg: `Successfully ${isCreate ? "created" : "updated"} connector`,
          theme: "success",
          timeout: 3000
        })
      )

      // Show endpoint URL for incoming connectors
      if (updateOrCreateResponse?.endpoint_url && isCreate) {
        dispatch(
          pushToast({
            msg: `Webhook URL: ${updateOrCreateResponse.endpoint_url}`,
            theme: "info",
            timeout: 10000
          })
        )
      }

      resetUpdateOrCreateResponse()
      formRef.current?.resetForm()
      onClose()
    }
  }, [
    dispatch,
    isCreate,
    isSuccess,
    onClose,
    resetUpdateOrCreateResponse,
    updateOrCreateResponse
  ])

  useEffect(() => {
    if (isError || updateOrCreateResponse?.status === "error") {
      console.log("Update/create error response:", updateOrCreateResponse)

      dispatch(
        pushToast({
          msg:
            updateOrCreateResponse?.message ||
            `Failed to ${isCreate ? "create" : "update"} connector`,
          theme: "danger",
          timeout: 3000
        })
      )
      resetUpdateOrCreateResponse()
    }
  }, [
    dispatch,
    isCreate,
    isError,
    resetUpdateOrCreateResponse,
    updateOrCreateResponse
  ])

  // Don't render form until we have data for edit mode
  if (open && !isCreate && !data) {
    return (
      <SidePanel title="Loading..." open={open} onClose={onClose} actions={[]}>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading connector details...</div>
        </div>
      </SidePanel>
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      innerRef={formRef}
      validationSchema={ConnectorSchema}
      enableReinitialize
    >
      {({ values, isValid, dirty, errors }) => {
        // Test and Logs tabs only available after connector is saved
        const showTestTab = !isCreate
        const showLogsTab = !isCreate

        const tabs = [
          { id: "details", label: "Details" },
          ...(showTestTab ? [{ id: "test", label: "Test" }] : []),
          ...(showLogsTab ? [{ id: "logs", label: "Logs" }] : [])
        ]

        // Simple hash-based change detection (exclude read-only fields)
        const currentHash = stableStringify({
          id: values.id,
          name: values.name,
          type: values.type,
          brand_id: values.brand_id,
          owner: values.owner,
          direction: values.direction,
          data_type: values.data_type,
          webhook_url: values.webhook_url,
          event: values.event,
          field_mapping: values.field_mapping,
          template: values.template,
          config: values.config,
          active: values.active
        })

        const initialHash = stableStringify({
          id: initialValues.id,
          name: initialValues.name,
          type: initialValues.type,
          brand_id: initialValues.brand_id,
          owner: initialValues.owner,
          direction: initialValues.direction,
          data_type: initialValues.data_type,
          webhook_url: initialValues.webhook_url,
          event: initialValues.event,
          field_mapping: initialValues.field_mapping,
          template: initialValues.template,
          config: initialValues.config,
          active: initialValues.active
        })

        const hasChanges = currentHash !== initialHash

        return (
          <SidePanel
            title={
              isCreate ? "Create Connector" : `Edit Connector - ${data?.name}`
            }
            open={open}
            onClose={onClose}
            actions={
              activeTab === "details"
                ? [
                    {
                      label: isCreate ? "Create Connector" : "Save Changes",
                      type: "primary",
                      onClick: onSubmit,
                      disabled: !(isValid && hasChanges)
                    }
                  ]
                : []
            }
          >
            {showTestTab && (
              <Tabs
                currentTab={activeTab}
                tabs={tabs}
                onChange={setActiveTab}
                className="mb-4"
              />
            )}

            {activeTab === "details" && (
              <ManageConnectorFormContent
                values={values}
                brands={brands ?? []}
                setFormField={setFormField}
                isCreate={isCreate}
                errors={errors}
                validateForm={() => formRef.current?.validateForm()}
                setFieldTouched={(field, touched) =>
                  formRef.current?.setFieldTouched(field, touched)
                }
              />
            )}

            {activeTab === "test" && (
              <div className="pt-4">
                <TestConnector values={values} isCreate={isCreate} />
              </div>
            )}

            {activeTab === "logs" && (
              <div className="pt-4">
                <ConnectorLogs connectorId={values.id || ""} />
              </div>
            )}
          </SidePanel>
        )
      }}
    </Formik>
  )
}

export default ManageConnector
